import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { PasswordStrengthIndicator } from '../auth/PasswordStrengthIndicator';
import { mapCognitoError } from '../../utils/cognitoErrors';
import { PRIVACY_URL, TERMS_URL } from '../../config/legal';
import '../../pages/vault/vault.css';

export type AuthTab = 'signup' | 'login';
type Step = 'form' | 'verify' | 'forgot' | 'reset';

interface ConsumerAuthFormsProps {
  defaultTab?: AuthTab;
  // When saving a piece, the sign-up form asks whether this brand may send news.
  brandName?: string;
  /** The visitor is signed in. `allowBrand`: they ticked the brand's box at sign-up. */
  onDone: (result: { allowBrand: boolean }) => void;
}

const Field: React.FC<{ id: string; label: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ id, label, children, aside }) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between px-0.5">
      <label htmlFor={id} className="text-[13px] font-medium text-[var(--v-text-2)]">
        {label}
      </label>
      {aside}
    </div>
    {children}
  </div>
);

// Create account / log in / confirm the email code / reset a forgotten
// password, all in place, so nobody is sent to the desktop-only pages.
const ConsumerAuthForms: React.FC<ConsumerAuthFormsProps> = ({ defaultTab = 'signup', brandName, onDone }) => {
  const { registerConsumer, confirmUserSignUp, resendConfirmationCode, login, forgotPassword, confirmPasswordReset } = useAuth();

  const [tab, setTab] = useState<AuthTab>(defaultTab);
  const [step, setStep] = useState<Step>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [allowBrand, setAllowBrand] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await fn();
    } catch (err) {
      setError(mapCognitoError(err as any).message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchTab = (next: AuthTab) => {
    setTab(next);
    setStep('form');
    setError(null);
    setNotice(null);
  };

  const signup = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      await registerConsumer(email.trim(), password);
      setStep('verify');
    });
  };

  const verify = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    run(async () => {
      await confirmUserSignUp(email.trim(), code);
      await login(email.trim(), password);
      onDone({ allowBrand });
    });
  };

  const signIn = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      await login(email.trim(), password);
      onDone({ allowBrand: false });
    });
  };

  const sendResetCode = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      await forgotPassword(email.trim());
      setCode('');
      setPassword('');
      setStep('reset');
      setNotice(`We sent a code to ${email.trim()}.`);
    });
  };

  const resetAndSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    run(async () => {
      await confirmPasswordReset(email.trim(), code, password);
      await login(email.trim(), password);
      onDone({ allowBrand: false });
    });
  };

  const resend = () =>
    run(async () => {
      await resendConfirmationCode(email.trim());
      setNotice('A new code is on its way.');
    });

  const codeInput = (
    <input
      id="vault-code"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus
      maxLength={6}
      value={code}
      onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
      placeholder="000000"
      className="v-input v-num !h-14 text-center !text-[22px] font-semibold tracking-[0.5em]"
    />
  );

  const submit = (label: string) => (
    <button type="submit" disabled={submitting} className="v-btn v-btn-primary w-full">
      {submitting && <Loader2 size={17} className="animate-spin" />}
      {label}
    </button>
  );

  const messages = (
    <>
      {error && (
        <p role="alert" className="rounded-xl px-3 py-2 text-[14px]" style={{ background: 'var(--v-danger-wash)', color: 'var(--v-danger)' }}>
          {error}
        </p>
      )}
      {notice && <p className="rounded-xl px-3 py-2 text-[14px] text-[var(--v-text-2)]" style={{ background: 'var(--v-glass-2)' }}>{notice}</p>}
    </>
  );

  if (step === 'verify') {
    return (
      <form onSubmit={verify} className="space-y-4">
        <div>
          <h3 className="v-display text-[22px] font-semibold">Check your email</h3>
          <p className="mt-1 text-[14.5px] text-[var(--v-text-2)]">
            We sent a 6-digit code to <span className="text-[var(--v-text)]">{email}</span>.
          </p>
        </div>
        {messages}
        <label htmlFor="vault-code" className="sr-only">
          Code
        </label>
        {codeInput}
        {submit('Verify and continue')}
        <button type="button" onClick={resend} disabled={submitting} className="w-full py-2 text-[14px] text-[var(--v-text-2)] hover:text-[var(--v-text)]">
          No code? Send another
        </button>
      </form>
    );
  }

  if (step === 'forgot' || step === 'reset') {
    return (
      <form onSubmit={step === 'forgot' ? sendResetCode : resetAndSignIn} className="space-y-4">
        <div>
          <h3 className="v-display text-[22px] font-semibold">Reset your password</h3>
          <p className="mt-1 text-[14.5px] text-[var(--v-text-2)]">
            {step === 'forgot' ? "Enter your email and we'll send you a code." : 'Enter the code and choose a new password.'}
          </p>
        </div>
        {messages}
        {step === 'forgot' ? (
          <Field id="vault-reset-email" label="Email">
            <input id="vault-reset-email" type="email" autoComplete="email" autoFocus required value={email} onChange={e => setEmail(e.target.value)} className="v-input" placeholder="you@example.com" />
          </Field>
        ) : (
          <>
            <Field id="vault-code" label="Code">
              {codeInput}
            </Field>
            <Field id="vault-new-password" label="New password">
              <input id="vault-new-password" type="password" autoComplete="new-password" required value={password} onChange={e => setPassword(e.target.value)} className="v-input" />
            </Field>
            <div className="[&_.bg-gray-200]:bg-[var(--v-glass-3)] [&_.text-gray-500]:text-[var(--v-text-3)] [&_.text-gray-700]:text-[var(--v-text-2)] [&_span]:text-[var(--v-text-2)]">
              <PasswordStrengthIndicator password={password} showRequirements={false} />
            </div>
          </>
        )}
        {submit(step === 'forgot' ? 'Send me a code' : 'Save and log in')}
        <button type="button" onClick={() => switchTab('login')} className="w-full py-2 text-[14px] text-[var(--v-text-2)] hover:text-[var(--v-text)]">
          Back to log in
        </button>
      </form>
    );
  }

  return (
    <div>
      <div className="v-seg v-glass" role="tablist" aria-label="Account">
        <span className="v-seg-thumb" style={{ width: 'calc((100% - 8px) / 2)', transform: `translateX(${tab === 'signup' ? 0 : 100}%)` }} aria-hidden="true" />
        <button type="button" role="tab" aria-selected={tab === 'signup'} aria-pressed={tab === 'signup'} onClick={() => switchTab('signup')}>
          Create account
        </button>
        <button type="button" role="tab" aria-selected={tab === 'login'} aria-pressed={tab === 'login'} onClick={() => switchTab('login')}>
          Log in
        </button>
      </div>

      <form onSubmit={tab === 'signup' ? signup : signIn} className="mt-5 space-y-4">
        {messages}
        <Field id="vault-email" label="Email">
          <input id="vault-email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="v-input" placeholder="you@example.com" />
        </Field>
        <Field
          id="vault-password"
          label="Password"
          aside={
            tab === 'login' ? (
              <button type="button" onClick={() => { setStep('forgot'); setError(null); }} className="text-[13px] font-medium text-[var(--v-accent-ink)]">
                Forgot password?
              </button>
            ) : undefined
          }
        >
          <input
            id="vault-password"
            type="password"
            autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="v-input"
          />
        </Field>
        {tab === 'signup' && (
          <>
            <div className="[&_.bg-gray-200]:bg-[var(--v-glass-3)] [&_.text-gray-500]:text-[var(--v-text-3)] [&_.text-gray-700]:text-[var(--v-text-2)] [&_span]:text-[var(--v-text-2)]">
              <PasswordStrengthIndicator password={password} showRequirements={false} />
            </div>
            {brandName && (
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl p-3 text-[14px] leading-snug" style={{ background: 'var(--v-glass)' }}>
                <input
                  type="checkbox"
                  checked={allowBrand}
                  onChange={e => setAllowBrand(e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-[var(--v-edge-2)] bg-transparent text-[var(--v-accent)] focus:ring-[var(--v-accent)]"
                />
                <span>
                  Let {brandName} send me news
                  <span className="block text-[12.5px] text-[var(--v-text-3)]">They never see who you are. Change it any time in Settings.</span>
                </span>
              </label>
            )}
            <p className="text-[12.5px] leading-snug text-[var(--v-text-3)]">
              By creating an account you accept the{' '}
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Terms</a> and the{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">Privacy Policy</a>.
            </p>
          </>
        )}
        {submit(tab === 'signup' ? 'Create my vault' : 'Log in')}
      </form>
    </div>
  );
};

export default ConsumerAuthForms;
